import { useApolloClient, useMutation } from '@base-app/web2'
import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_LOCATION, UPDATE_LOCATION } from '../../../graphql/locations/mutations'
import { LOCATION_QUERY, LOCATIONS_QUERY } from '../../../graphql/locations/queries'
import type { Location } from '../../../graphql/locations/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { refetchQuietly } from '../../../lib/apolloRefetch'
import { dropSearchedLists, prependToLists } from '../../../lib/cacheLists'
import {
  focusFieldById,
  isRichTextTooLongError,
  isSortOrderInvalidError,
  isSubtitleTooLongError,
  MAX_RICH_TEXT_LENGTH,
} from '../../../lib/richText'
import { MAX_SORT_ORDER, parseSortOrder } from '../../../lib/sortOrder'
import { isSubtitleTooLong } from '../../../lib/subtitle'
import { lowResPathFor } from '../../../lib/uploads'
import { Button, FormActions } from '../../atoms'
import {
  FormField,
  FormLoadState,
  ImageUploadField,
  RichTextField,
  TextareaField,
} from '../../molecules'

interface LocationFormFieldsProps {
  location?: Location
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const LocationFormFields = memo(
  ({ location, clientId, appId, onSuccess, onClose, skeleton }: LocationFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const client = useApolloClient()
    const isEdit = !!location
    const [name, setName] = useState(location?.name ?? '')
    const [address, setAddress] = useState(location?.address ?? '')
    const [subtitle, setSubtitle] = useState(location?.subtitle ?? '')
    const [description, setDescription] = useState(location?.description ?? '')
    const [image, setImage] = useState(location?.image ?? '')
    const [uploadingImage, setUploadingImage] = useState(false)
    const [observations, setObservations] = useState(location?.observations ?? '')
    const [sortOrder, setSortOrder] = useState(
      location?.sort_order === null || location?.sort_order === undefined
        ? ''
        : String(location.sort_order),
    )
    const [errors, setErrors] = useState<{
      name?: string
      subtitle?: string
      description?: string
      sortOrder?: string
    }>({})

    const makeOptimistic = useCallback(
      (id: string): Location => ({
        __typename: 'Location',
        id,
        client_id: clientId,
        application_id: appId,
        name,
        address: address || null,
        subtitle: subtitle.trim() || null,
        description: description || null,
        image: image || null,
        image_low_res: lowResPathFor(image),
        observations: observations || null,
        sort_order: parseSortOrder(sortOrder) ?? null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [clientId, appId, name, address, subtitle, description, image, observations, sortOrder],
    )

    // The API's refusals belong to their fields; anything else is a toast
    const handleError = useCallback(
      (err: Error) => {
        if (isRichTextTooLongError(err)) {
          setErrors(current => ({
            ...current,
            description: t('locations.form.descriptionTooLong'),
          }))
          focusFieldById('loc-description')
        } else if (isSubtitleTooLongError(err)) {
          setErrors(current => ({ ...current, subtitle: t('locations.form.subtitleTooLong') }))
          focusFieldById('loc-subtitle')
        } else if (isSortOrderInvalidError(err)) {
          setErrors(current => ({ ...current, sortOrder: t('common.form.sortOrderInvalid') }))
          focusFieldById('loc-sort-order')
        } else {
          showToast(err.message, 'error')
        }
      },
      [showToast, t],
    )

    // The API orders by sort order, then name: the prepend is only the optimistic first guess, and an
    // update can move a row. Drop the searched lists and refetch the open ones once the server answered
    // (one extra list request per save, on purpose)
    const refreshLists = useCallback(() => {
      dropSearchedLists(client.cache, 'locations', { clientId, applicationId: appId })
      refetchQuietly(client.refetchQueries({ include: [LOCATIONS_QUERY] }))
    }, [client, clientId, appId])

    const [createLocation, { loading: creating }] = useMutation(CREATE_LOCATION, {
      optimisticResponse: { createLocation: makeOptimistic(`temp-${Date.now()}`) },
      update(cache, result) {
        const item = result.data?.createLocation
        if (!item) {
          return
        }
        prependToLists(cache, 'locations', item, { clientId, applicationId: appId })
      },
      onError: handleError,
      onCompleted() {
        refreshLists()
        onSuccess()
      },
    })

    const [updateLocation, { loading: updating }] = useMutation(UPDATE_LOCATION, {
      optimisticResponse: { updateLocation: makeOptimistic(location?.id ?? '') },
      onError: handleError,
      onCompleted() {
        refreshLists()
        onSuccess()
      },
    })

    const loading = creating || updating || uploadingImage

    const validate = useCallback(() => {
      const errs: { name?: string; subtitle?: string; description?: string; sortOrder?: string } =
        {}
      if (!name.trim()) {
        errs.name = t('common.form.nameRequired')
      }
      if (isSubtitleTooLong(subtitle)) {
        errs.subtitle = t('locations.form.subtitleTooLong')
      }
      if (description.length > MAX_RICH_TEXT_LENGTH) {
        errs.description = t('locations.form.descriptionTooLong')
      }
      if (parseSortOrder(sortOrder) === undefined) {
        errs.sortOrder = t('common.form.sortOrderInvalid')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [name, subtitle, description, sortOrder, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const vars = {
          name,
          address: address || null,
          subtitle: subtitle.trim() || null,
          description: description || null,
          image: image || null,
          observations: observations || null,
          sort_order: parseSortOrder(sortOrder) ?? null,
        }
        if (isEdit) {
          updateLocation({ variables: { id: location.id, ...vars } })
        } else {
          createLocation({ variables: { client_id: clientId, application_id: appId, ...vars } })
        }
      },
      [
        validate,
        isEdit,
        location?.id,
        name,
        address,
        subtitle,
        description,
        image,
        observations,
        sortOrder,
        clientId,
        appId,
        updateLocation,
        createLocation,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <FormField
          skeleton={skeleton}
          id="loc-name"
          label={t('locations.form.nameLabel')}
          name="name"
          value={name}
          onChange={e => setName(e.target.value)}
          error={errors.name}
          required
        />
        <FormField
          skeleton={skeleton}
          id="loc-address"
          label={t('locations.form.addressLabel')}
          name="address"
          value={address}
          onChange={e => setAddress(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="loc-subtitle"
          label={t('locations.form.subtitleLabel')}
          name="subtitle"
          value={subtitle}
          onChange={e => setSubtitle(e.target.value)}
          error={errors.subtitle}
        />
        <ImageUploadField
          skeleton={skeleton}
          applicationId={appId}
          id="loc-image-file"
          target="locations"
          label={t('locations.form.imageLabel')}
          value={image}
          onChange={setImage}
          onUploadingChange={setUploadingImage}
        />
        <RichTextField
          skeleton={skeleton}
          id="loc-description"
          label={t('locations.form.descriptionLabel')}
          value={description}
          onChange={setDescription}
          error={errors.description}
        />
        <FormField
          skeleton={skeleton}
          id="loc-sort-order"
          label={t('common.form.sortOrderLabel')}
          name="sort_order"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={String(MAX_SORT_ORDER).length}
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
          error={errors.sortOrder}
          hint={t('common.form.sortOrderHint')}
        />
        <TextareaField
          skeleton={skeleton}
          id="loc-observations"
          label={t('locations.form.observationsLabel')}
          name="observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('locations.form.saving') : t('locations.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

LocationFormFields.displayName = 'LocationFormFields'

interface LocationFormProps extends Omit<LocationFormFieldsProps, 'location'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const LocationForm = memo(({ id, ...props }: LocationFormProps) => {
  const { record, status } = useRecordById(LOCATION_QUERY, id, data => data.location)
  if (status === 'loading') {
    return <LocationFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <LocationFormFields key={id ?? 'new'} location={record} {...props} />
})

LocationForm.displayName = 'LocationForm'
